<template>
  <v-simple-table dense style="width:100%;">
    <template v-slot:default>
      <thead>
        <tr>
          <th rowspan="2" class="text-xs-left pl-2 pr-0 yellow lighten-4">
            <v-row>
              <v-col class="py-4">MRC Job Class</v-col>
              <v-col cols="3">
                <v-menu
                  :close-on-content-click="false"
                  :nudge-width="200"
                  v-model="filterVisible"
                  offset-x
                >
                  <template v-slot:activator="{ on }">
                    Location
                    <v-btn v-on="on" icon text color="primary">
                      <v-icon>filter_list</v-icon>
                    </v-btn>
                  </template>
                  <v-card class="px-4">
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn color="primary" icon text @click="saveCountrySelected">
                        <v-icon>save</v-icon>
                      </v-btn>
                    </v-card-actions>
                    <!-- eslint-disable-next-line -->
                    <v-autocomplete
                      chips
                      deletable-chips
                      multiple
                      dense
                      :items="countriesWithHeader"
                      item-text="countryName"
                      item-value="countryCode"
                      @change="tempCountriesSelected = $event"
                      v-model="countriesSelectedLocal"
                      return-object
                      hide-details
                      class="pt-0 pb-4"
                    >
                      <template slot="item" slot-scope="data">
                        <v-list-item-content
                          v-if="data.item.header"
                          v-text="data.item"
                        ></v-list-item-content>
                        <v-divider v-else-if="data.item.divider" inset></v-divider>
                        <v-list-item-content v-else class="pb-2">
                          <v-list-item-title v-html="data.item.countryName"></v-list-item-title>
                          <v-list-item-subtitle v-html="data.item.region"></v-list-item-subtitle>
                        </v-list-item-content>
                      </template>
                    </v-autocomplete>
                  </v-card>
                </v-menu>
              </v-col>
            </v-row>
          </th>
          <th
            :colspan="countriesSelected.length"
            class="grey lighten-3 text-center"
          >
            Annual Total Cost
          </th>
        </tr>
        <tr class="border">
          <th class="text-xs-left px-8" v-for="c in countriesSelected" :key="c.countryCode">
            {{ c.countryName }}
          </th>
        </tr>
        <tr class="pa-0" v-if="minMaxLoading">
          <th :colspan="countriesSelected.length + 1" class="pa-0" style="height:4px;">
            <v-progress-linear indeterminate></v-progress-linear>
          </th>
        </tr>
      </thead>
      <tbody>
        <template v-if="!minMaxLoading && minMaxJobClasses.min === -1">
          <tr>
            <td :colspan="countriesSelected.length + 1" class="">
              <div class="v-alert error">
                <i aria-hidden="true" class="v-icon material-icons v-alert__icon">warning</i>
                <div>
                  No data available for your selection. Please select another Sector, Job Group or
                  Locations
                </div>
              </div>
            </td>
          </tr>
        </template>
        <template
          v-if="!minMaxLoading && onetJobGroupSelected.jobGroup && minMaxJobClasses.min >= 0"
        >
          <tr v-for="j in jobClassRange" :key="j.jobClass">
            <td class="pr-0">{{ j.jobClass }}</td>
            <td class="text-xs-left" v-for="c in countriesSelected" :key="c.countryCode">
              <SalaryRange :country="c" :jobClass="j.jobClass" :exchangeRate="currency">
              </SalaryRange>
            </td>
          </tr>
        </template>
      </tbody>
    </template>
  </v-simple-table>
</template>

<script>
import Vue from 'vue';
import { mapState, mapActions } from 'vuex';
import SalaryRange from './SalaryRange.vue';

export default Vue.extend({
  props: {
    currency: {
      type: Object,
    },
    countries: {
      default() {
        return [];
      },
      type: Array,
    },
  },
  computed: {
    countriesWithHeader() {
      const countriesWithHeader = [];
      if (this.countries.length === 0) {
        return countriesWithHeader;
      }
      countriesWithHeader.push({ header: this.countries[0].continent });
      this.countries.forEach((c, i) => {
        if (i !== 0 && c.continent !== this.countries[i - 1].continent) {
          countriesWithHeader.push({ divider: true });
          countriesWithHeader.push({ header: c.continent });
        }
        countriesWithHeader.push({
          countryName: c.countryName,
          countryCode: c.countryCode,
          region: c.region,
          continent: c.continent,
          __typename: c.__typename, // eslint-disable-line
        });
      });
      return countriesWithHeader;
    },
    jobClassRange() {
      const range = [];
      if (
        this.minMaxJobClasses.min > this.minMaxJobClasses.max
        || this.minMaxJobClasses.min === -1
        || this.minMaxJobClasses.max === -1
      ) {
        return range;
      }
      for (let i = this.minMaxJobClasses.max; i >= this.minMaxJobClasses.min; i -= 1) {
        range.push({ jobClass: i });
      }
      return range;
    },
    ...mapState({
      sectorSelected: (state) => state.sectorSelected,
      onetJobGroupSelected: (state) => state.onetJobGroupSelected,
      countriesSelected: (state) => state.countriesSelected,
      minMaxJobClasses: (state) => state.minMaxJobClasses,
      minMaxLoading: (state) => state.minMaxLoading,
    }),
  },
  methods: {
    saveCountrySelected() {
      const items = this.countriesSelectedLocal.map((c) => ({
        countryCode: c.countryCode,
        countryName: c.countryName,
      }));
      this.mutateCountriesSelected(items).then((data) => {
        if (data) {
          this.countriesSelectedLocal = items;
        }
      });
      this.filterVisible = false;
    },
    ...mapActions(['queryCountriesSelected', 'mutateCountriesSelected', 'queryMinMaxJobClasses']),
  },
  created() {
    this.queryCountriesSelected().then((data) => {
      this.countriesSelectedLocal = data;
    });
    this.$watch(
      () => `name: ${this.sectorSelected.name} - jobgroup: ${
        this.onetJobGroupSelected.jobGroup
      } - countryCode: ${this.countriesSelected.map((c) => c.countryCode).join(',')}`,
      () => {
        const cond = this.sectorSelected.name
          && this.onetJobGroupSelected.jobGroup
          && this.onetJobGroupSelected.jobGroup.length > 0
          && this.countriesSelected
          && this.countriesSelected.length > 0;
        if (cond) {
          this.queryMinMaxJobClasses({
            sectorName: this.sectorSelected.name,
            onetJobGroup: this.onetJobGroupSelected.jobGroup,
            countryCodes: this.countriesSelected.map((c) => c.countryCode),
          });
        }
      },
    );
  },
  watch: {
    filterVisible() {
      if (!this.filterVisible) {
        this.countriesSelectedLocal = this.countriesSelected;
      }
    },
  },
  data() {
    return {
      countriesSelectedLocal: [],
      filterVisible: false,
    };
  },
  components: {
    SalaryRange,
  },
});
</script>
<style scoped>
/* .v-table thead th {
  color: rgba(0, 0, 0, 0.54);
} */
/* .v-table tr:not(:last-child) {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}
.v-table tbody tr:hover:not(.v-datatable__expand-row) {
  background: #eee;
} */
tr:not(:hover) td:first-child {
  background-color: #fffde7;
}
th:nth-child(2n + 3) {
  background-color: #fafafa;
}
th:nth-child(even) {
  background-color: #eee;
}
/* tr.border {
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
} */
</style>
